import { capitalize } from 'lodash'
import { useMemo, useState } from 'react'
import { twMerge as twm } from 'tailwind-merge'

import { Booking } from '../../models/Booking'
import { Item, createAvailabilitySummary } from '../../models/Item'
import { Transaction } from '../../models/Transaction'
import { BookableDefaultColorKey } from '../../utils/calendarColors'
import { Icon } from '../Icon'
import { PSApplication } from '../app/PSApplication'
import Button from '../app/design/Button'
import Modal from '../app/design/Modal'
import Paragraph from '../app/design/Paragraph'
import { CalendarColorSelect } from '../calendar/CalendarColorSelect'
import { ContractSearchableSelect } from './ContractSearchableSelect'
import { TypeSearchableSelect } from './TypeSearchableSelect'
import { HourForm, ScheduleForm } from '../common/HourForm'
import { ItemLocationChips } from './ItemLocationChips'
import { ConfirmationModal } from '../common/modals/ConfirmationModal'
import { VideoConferenceChips } from '../settings/VideoConferenceChips'
import { Checkbox } from '../common/fields/Checkbox'
import { CurrencyField } from '../common/fields/CurrencyField'
import { Select } from '../common/fields/Select'
import { TextArea } from '../common/fields/TextArea'
import { TextField } from '../common/fields/TextField'
import { createAvailabilityArrayV2 } from '../settings/SetupBusinessHoursModalV1'
import { SelectedPhoto } from '../workflows/PhotoPicker'
import { WorkflowPage } from '../workflows/WorkflowPage'

type Props = {
    item: Item
    onDelete?: () => void
    onClose?: () => void
    isLoading?: boolean
    onSave: (item: Item, photo?: File) => void
    enableAllFields?: boolean
}

const buildAvailabilitySummary = (item: Item) => {
    const weekdays = createAvailabilityArrayV2(item)
    return createAvailabilitySummary(weekdays)
}

const buildScheduleForm = (item: Item): ScheduleForm => {
    return {
        monStart: item.monStartHour,
        monEnd: item.monEndHour,
        tueStart: item.tueStartHour,
        tueEnd: item.tueEndHour,
        wedStart: item.wedStartHour,
        wedEnd: item.wedEndHour,
        thuStart: item.thuStartHour,
        thuEnd: item.thuEndHour,
        friStart: item.friStartHour,
        friEnd: item.friEndHour,
        satStart: item.satStartHour,
        satEnd: item.satEndHour,
        sunStart: item.sunStartHour,
        sunEnd: item.sunEndHour,
        schedule: item.schedule ?? 0x0,
    }
}

export const ServicePage = ({
    item,
    onDelete,
    onClose,
    isLoading,
    onSave,
    enableAllFields = true,
}: Props) => {
    const owner = PSApplication.owner()
    const [guardrail, setGuardrail] = useState<
        undefined | 'singleCapacity' | 'availability' | 'location' | 'price'
    >(undefined)

    const [scratchItem, setScratchItem] = useState(item)

    const [selectedPhoto, setSelectedPhoto] = useState<SelectedPhoto | null>(
        item.image?.uri ? { type: 'url', url: item.image.url ?? '' } : null
    )

    const availabilitySummary = useMemo(() => buildAvailabilitySummary(item), [item])
    const [scratchScheduleForm, setScratchScheduleForm] = useState<ScheduleForm>(
        buildScheduleForm(item)
    )

    const isNextEnabled = useMemo(() => {
        return scratchItem?.name
    }, [scratchItem])

    return (
        <WorkflowPage
            title={capitalize(item.type ?? '')}
            footerContent={
                <div
                    className={twm(
                        'flex w-full items-center gap-x-4',
                        onDelete !== undefined ? 'justify-between' : 'justify-end'
                    )}
                >
                    {onDelete !== undefined && (
                        <Button
                            intent="secondary"
                            className="text-danger-400"
                            onClick={onDelete}
                            isLoading={isLoading}
                        >
                            Delete {item.type}
                        </Button>
                    )}

                    <Button
                        intent="primary"
                        onClick={() => {
                            if (Number.isNaN(scratchItem.rate)) {
                                setGuardrail('price')
                            }
                            else {
                                onSave(
                                    scratchItem,
                                    selectedPhoto?.type === 'file' ? selectedPhoto.file : undefined
                                )
                            }
                        }}
                        disabled={isLoading || !isNextEnabled}
                        isLoading={isLoading}
                    >
                        Save
                    </Button>
                </div>
            }
            onClose={onClose}
        >
            <div className="mt-12 flex w-full flex-col items-center justify-start gap-y-6">
                <WorkflowPage.Card title="General">
                    {enableAllFields && (
                        <WorkflowPage.PhotoPicker
                            onSelectPhoto={(photoFile) => {
                                setSelectedPhoto({ type: 'file', file: photoFile })
                            }}
                            onClearPhoto={() => setSelectedPhoto(null)}
                            selectedPhoto={selectedPhoto}
                        />
                    )}
                    <WorkflowPage.IconSection
                        title="Name (required)"
                        icon={<Icon icon="tag.large" background="round" />}
                    >
                        <TextField
                            isFullWidth
                            size="medium"
                            placeholder="Enter name"
                            value={scratchItem?.name}
                            onClear={() => setScratchItem((i) => i?.clone({ name: '' }))}
                            onChange={(e) => {
                                setScratchItem((i) => i?.clone({ name: e.target.value }))
                            }}
                        />
                    </WorkflowPage.IconSection>
                    <WorkflowPage.IconSection
                        title="Price"
                        icon={<Icon icon="dollar.large" background="round" />}
                    >
                        <CurrencyField
                            isFullWidth
                            size="medium"
                            placeholder="0.00"
                            value={scratchItem?.rate}
                            onClear={() => setScratchItem((i) => i?.clone({ rate: undefined }))}
                            min={0}
                            step={0.01}
                            icon={'dollar9.large'}
                            onChange={(e) => {
                                setScratchItem((i) => i?.clone({ rate: e }))
                            }}
                        />
                    </WorkflowPage.IconSection>
                    <WorkflowPage.IconSection
                        title="Deposit"
                        icon={<Icon icon="dollar3.large" background="round" />}
                    >
                        <CurrencyField
                            isFullWidth
                            size="medium"
                            placeholder="0.00"
                            value={scratchItem?.deposit}
                            onClear={() => setScratchItem((i) => i?.clone({ deposit: undefined }))}
                            min={0}
                            step={0.01}
                            icon={'dollar9.large'}
                            onChange={(e) => {
                                setScratchItem((i) => i?.clone({ deposit: e }))
                            }}
                        />
                    </WorkflowPage.IconSection>
                    {owner.allowDiscount && (
                        <WorkflowPage.IconSection
                            title="Discount rate"
                            icon={<Icon icon="discount.large" background="round" />}
                        >
                            <Select
                                size="medium"
                                selectedOptionID={scratchItem?.discount?.toString() || ''}
                                onSelectOption={(option) => {
                                    setScratchItem((i) => i?.clone({ discount: option.id }))
                                }}
                                options={Transaction.discountRateStrings().map((option) => ({
                                    id: option.id ?? '',
                                    title: option.label ?? '',
                                }))}
                            />
                        </WorkflowPage.IconSection>
                    )}
                    {owner.allowSalesTax && (
                        <WorkflowPage.IconSection
                            title="Allow sales tax"
                            description="Automatically calculate & apply sales tax"
                            icon={<Icon icon="percentage2.large" background="round" />}
                            hasHoverEffect
                            secondaryView={
                                <Checkbox
                                    isChecked={scratchItem.allowSalesTax}
                                    onChange={(e) =>
                                        setScratchItem((i) => i?.clone({ allow_salestax: e }))
                                    }
                                />
                            }
                        />
                    )}
                    <WorkflowPage.IconSection
                        title="Description"
                        icon={<Icon icon="note.large" background="round" />}
                    >
                        <TextArea
                            size="medium"
                            placeholder="Enter description"
                            isAutoResizing
                            value={scratchItem?.memo}
                            onClear={() => setScratchItem((i) => i?.clone({ memo: '' }))}
                            onChange={(e) => {
                                setScratchItem((i) => i?.clone({ memo: e.target.value }))
                            }}
                        />
                    </WorkflowPage.IconSection>

                    {enableAllFields && (
                        <WorkflowPage.IconSection
                            title="Calendar color"
                            description="Color will be associated with this service on your calendar"
                            icon={<Icon icon="palette.large" background="round" />}
                        >
                            <CalendarColorSelect
                                type={item.type as BookableDefaultColorKey}
                                color={scratchItem?.color ?? ''}
                                onSelect={(color) => {
                                    setScratchItem((i) => i?.clone({ color }))
                                }}
                            />
                        </WorkflowPage.IconSection>
                    )}
                </WorkflowPage.Card>

                {item.type !== 'package' && (
                    <WorkflowPage.Card title="Scheduling">
                        <WorkflowPage.IconSection
                            title="Duration"
                            description="How long does the service take?"
                            icon={<Icon icon="clock.large" background="round" />}
                        >
                            <Select
                                size="medium"
                                options={Booking.durationStrings(false, false).map((option) => ({
                                    id: option.id ?? '',
                                    title: option.label ?? '',
                                }))}
                                selectedOptionID={scratchItem?.duration?.toString() ?? 'TBD'}
                                onSelectOption={(option) => {
                                    setScratchItem((i) => i?.clone({ duration: option.id }))
                                }}
                            />
                        </WorkflowPage.IconSection>

                        <WorkflowPage.IconSection
                            title="Lead time"
                            description="Minimum amount of time needed to prep for this service"
                            icon={<Icon icon="stopwatch.large" background="round" />}
                        >
                            <Select
                                size="medium"
                                options={Item.leadtimeStrings(item?.type).map((option) => ({
                                    id: option.id ?? '',
                                    title: option.label ?? '',
                                }))}
                                selectedOptionID={scratchItem?.leadtime?.toString() ?? ''}
                                onSelectOption={(option) => {
                                    setScratchItem((i) => i?.clone({ leadtime: option.id }))
                                }}
                            />
                        </WorkflowPage.IconSection>

                        {owner.allowWidget && (
                            <WorkflowPage.IconSection
                                title="Show online"
                                description="Allow clients to purchase this service online"
                                icon={<Icon icon="computerClock.large" background="round" />}
                                hasHoverEffect
                                secondaryView={
                                    <Checkbox
                                        isChecked={scratchItem?.isPublic}
                                        onChange={(e) => {
                                            setScratchItem((i) => i?.clone({ is_public: e }))
                                        }}
                                    />
                                }
                            />
                        )}

                        <WorkflowPage.IconSection
                            title="Single capacity"
                            description="Only one client can book this equipment / room at a time"
                            icon={<Icon icon="user.large" background="round" />}
                            hasHoverEffect
                            secondaryView={
                                <Checkbox
                                    isChecked={scratchItem?.capacity === 1}
                                    onChange={(isChecked) => {
                                        if (isChecked) {
                                            setGuardrail('singleCapacity')
                                        }
                                        else {
                                            setScratchItem((i) => i?.clone({ capacity: undefined }))
                                        }
                                    }}
                                />
                            }
                        />

                        <WorkflowPage.IconSection
                            title="Availability"
                            description={availabilitySummary}
                            descriptionClassName="text-secondary-400"
                            hasHoverEffect
                            icon={<Icon icon="calendar.large" background="round" />}
                            secondaryView={
                                <Button
                                    intent="tertiaryLink"
                                    className="p-0"
                                    onClick={() => setGuardrail('availability')}
                                >
                                    Edit
                                </Button>
                            }
                        />
                    </WorkflowPage.Card>
                )}

                <WorkflowPage.Card title="Attachments">
                    <WorkflowPage.IconSection
                        title="Attach a contract"
                        description="This contract will be signed when a client purchases this service"
                        icon={<Icon icon="pen.large" background="round" />}
                    >
                        <ContractSearchableSelect
                            selectedContract={scratchItem?.contract}
                            onSelectContract={(contract) => {
                                setScratchItem((i) => i?.clone({ contract }))
                            }}
                        />
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection
                        title="Attach a form"
                        description="This form will be completed when a client purchases this service"
                        icon={<Icon icon="field.large" background="round" />}
                    >
                        <TypeSearchableSelect
                            owner={owner}
                            selectedForm={scratchItem?.form}
                            onSelectForm={(form) => {
                                setScratchItem((i) => i?.clone({ form }))
                            }}
                        />
                    </WorkflowPage.IconSection>
                </WorkflowPage.Card>

                <WorkflowPage.Card title="Location">
                    <WorkflowPage.IconSection
                        title="Add video conference"
                        description="Add a video conference link to this appointment"
                        icon={<Icon icon="video.large" background="round" />}
                    >
                        <VideoConferenceChips
                            onSelectOption={(option) => {
                                setScratchItem((i) =>
                                    i?.clone({
                                        conference_type: option.id,
                                        allow_conference: option.id !== '',
                                    })
                                )
                            }}
                            selectedOptionId={scratchItem?.conferenceType}
                            isClass={false}
                        />
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection
                        title="Location"
                        icon={<Icon icon="markerPin.large" background="round" />}
                    >
                        <ItemLocationChips item={scratchItem} onUpdateItem={setScratchItem} />
                    </WorkflowPage.IconSection>
                </WorkflowPage.Card>

                <ConfirmationModal
                    title="Would you like to continue without adding a price?"
                    isOpen={guardrail === 'price'}
                    onClose={() => setGuardrail(undefined)}
                    shouldHideCancelButton
                    onConfirm={() => {
                        onSave(
                            scratchItem,
                            selectedPhoto?.type === 'file' ? selectedPhoto.file : undefined
                        )
                        setGuardrail(undefined)
                    }}
                >
                    <Paragraph className="text-gray-600">
                        If you don’t add a price this service will be shown as a price of “TBD” and
                        allow you to edit the price on a service by service basis.
                    </Paragraph>
                    <Paragraph className="mt-4 text-gray-600">
                        This is great if you don’t necessarily know the price of the service before
                        booking it.
                    </Paragraph>
                </ConfirmationModal>

                <ConfirmationModal
                    title="Single capacity"
                    isOpen={guardrail === 'singleCapacity'}
                    onClose={() => setGuardrail(undefined)}
                    shouldHideCancelButton
                    onConfirm={() => {
                        setScratchItem((i) => i?.clone({ capacity: 1 }))
                        setGuardrail(undefined)
                    }}
                >
                    <Paragraph className="text-gray-600">
                        By turning on single capacity, this service can only be booked once.
                        Multiple clients will not be able to book this service in the same time
                        slot.
                    </Paragraph>
                </ConfirmationModal>

                <Modal
                    title="Availability"
                    size="large"
                    isOpen={guardrail === 'availability'}
                    footer={
                        <div className="flex w-full items-center justify-end gap-x-4">
                            <Button
                                intent="primary"
                                onClick={() => {
                                    setScratchItem((i) =>
                                        i?.clone({
                                            mon_start_hour: scratchScheduleForm.monStart,
                                            mon_end_hour: scratchScheduleForm.monEnd,
                                            tue_start_hour: scratchScheduleForm.tueStart,
                                            tue_end_hour: scratchScheduleForm.tueEnd,
                                            wed_start_hour: scratchScheduleForm.wedStart,
                                            wed_end_hour: scratchScheduleForm.wedEnd,
                                            thu_start_hour: scratchScheduleForm.thuStart,
                                            thu_end_hour: scratchScheduleForm.thuEnd,
                                            fri_start_hour: scratchScheduleForm.friStart,
                                            fri_end_hour: scratchScheduleForm.friEnd,
                                            sat_start_hour: scratchScheduleForm.satStart,
                                            sat_end_hour: scratchScheduleForm.satEnd,
                                            sun_start_hour: scratchScheduleForm.sunStart,
                                            sun_end_hour: scratchScheduleForm.sunEnd,
                                            schedule: scratchScheduleForm.schedule,
                                        })
                                    )
                                    setGuardrail(undefined)
                                }}
                            >
                                Save
                            </Button>
                        </div>
                    }
                    onClose={() => setGuardrail(undefined)}
                >
                    <div className="flex flex-col gap-6">
                        <Paragraph className="text-gray-600">
                            You do not need to set specific availability for your services unless
                            they are different from your overall business hours.
                        </Paragraph>
                        <HourForm
                            scheduleForm={scratchScheduleForm}
                            onUpdateScheduleForm={setScratchScheduleForm}
                        />
                    </div>
                </Modal>
            </div>
        </WorkflowPage>
    )
}
